import { FoulBuildUpRowData, CompoundConfig } from '../types/tender.types';
import { COMPOUND_ROW_DEFS, COMPOUND_SECTION_ROW_IDS } from './compoundRowDefs';
import { EVALUATE_CODES } from './constants';

// Excel column indices (0-based) — Description=C(2), Qty=D(3), EvalCode=F(5)
const DESC_COL = 2;
const QUANT_COL = 3;
const EVAL_COL = 5;

// Each of these section header rows reads "<Label> WxHm" (e.g. "Compound 20m
// x 20m", "Silo Base, 6m x 3m", "Carpark 32m x 23m", "Material store 24m x
// 46m") — the leading label word(s) distinguish which section's size it is.
type SizeConfigKey = 'compoundSizeW' | 'compoundSizeH' | 'siloBaseSizeW' | 'siloBaseSizeH'
  | 'carparkSizeW' | 'carparkSizeH' | 'materialStoreSizeW' | 'materialStoreSizeH';

const SIZE_PATTERNS: Array<{ pattern: RegExp; wKey: SizeConfigKey; hKey: SizeConfigKey }> = [
  { pattern: /^Compound\s+(\d+(?:\.\d+)?)\s*m?\s*x\s*(\d+(?:\.\d+)?)\s*m/i, wKey: 'compoundSizeW', hKey: 'compoundSizeH' },
  { pattern: /^Silo\s+Base,?\s+(\d+(?:\.\d+)?)\s*m?\s*x\s*(\d+(?:\.\d+)?)\s*m/i, wKey: 'siloBaseSizeW', hKey: 'siloBaseSizeH' },
  { pattern: /^Carpark\s+(\d+(?:\.\d+)?)\s*m?\s*x\s*(\d+(?:\.\d+)?)\s*m/i, wKey: 'carparkSizeW', hKey: 'carparkSizeH' },
  { pattern: /^Material\s+store\s+(\d+(?:\.\d+)?)\s*m?\s*x\s*(\d+(?:\.\d+)?)\s*m/i, wKey: 'materialStoreSizeW', hKey: 'materialStoreSizeH' },
];

const extractCompoundSizes = (rows: unknown[][]): CompoundConfig => {
  const config: CompoundConfig = {};
  for (const row of rows) {
    const desc = String(row[DESC_COL] ?? '').trim();
    for (const { pattern, wKey, hKey } of SIZE_PATTERNS) {
      if (config[wKey] !== undefined) continue;
      const match = desc.match(pattern);
      if (match) {
        config[wKey] = parseFloat(match[1]);
        config[hKey] = parseFloat(match[2]);
      }
    }
  }
  return config;
};

// Section detection: map normalised trigger keywords → section key
const SECTION_TRIGGERS: Array<{ pattern: RegExp; key: string }> = [
  { pattern: /COMPOUND\s+WORKS/i,           key: 'COMPOUND WORKS' },
  { pattern: /SILO\s+BASE/i,                key: 'SILO BASE' },
  { pattern: /CARPARK/i,                    key: 'CARPARK' },
  { pattern: /MATERIAL\s+STORE/i,           key: 'MATERIAL STORE' },
  { pattern: /REMOVAL[,\s]/i,               key: 'REMOVAL' },
  { pattern: /SALES\s+AREA/i,               key: 'SALES AREA' },
  { pattern: /TEMPORARY\s+HAUL\s+ROAD/i,    key: 'TEMPORARY HAUL ROAD' },
  { pattern: /^WALKWAYS\s*$/i,              key: 'WALKWAYS' },
];

const detectSectionTrigger = (row: unknown[], triggered: Set<string>): string | null => {
  const scan = Math.min(row.length, 8);
  for (let c = 0; c < scan; c++) {
    const val = String(row[c] ?? '').trim();
    if (!val) continue;
    for (const { pattern, key } of SECTION_TRIGGERS) {
      if (!triggered.has(key) && pattern.test(val)) {
        return key;
      }
    }
  }
  return null;
};

// COMPOUND WORKS row cw_3 ("Supply & Lay 6F2, XXXmm thick") — the mm value
// is read straight off this specific row's own description, not a blind
// sheet-wide regex scan, since Carpark/Material Store share the same text.
const SUPPLY_LAY_6F2_PATTERN = /Supply\s*&\s*Lay\s+6F2,?\s*(\d+(?:\.\d+)?)\s*mm\s*thick/i;

// COMPOUND WORKS row cw_4 ("Type 1, XXXmm thick") — same row-position
// approach, since Carpark/Material Store's Type 1 rows read "Supply and lay
// Type 1, XXXmm thick" (different text, but matched by id, not text, anyway).
const TYPE1_PATTERN = /Type\s+1,?\s*(\d+(?:\.\d+)?)\s*mm\s*thick/i;

// COMPOUND WORKS row cw_11 ("<grade> concrete, XXXmm thick with 1 layer
// <mesh> mesh") — grade/mm/mesh all read off this row's own description.
const CONCRETE_MESH_PATTERN = /^(\S+)\s+concrete,?\s*(\d+(?:\.\d+)?)\s*mm\s*thick\s+with\s+1\s+layer\s+(\S+)\s+mesh/i;

// SILO BASE rows sb_2/sb_3/sb_5/sb_6/sb_7 — each mm/grade/mesh value read off
// its own row's description, same row-position approach as the COMPOUND
// WORKS rows above.
const SILO_SUB_BASE_PATTERN = /^(\d+(?:\.\d+)?)\s*mm\s*6F2\s*Sub\s*Base/i;
const SILO_BLINDING_PATTERN = /^(\d+(?:\.\d+)?)\s*mm\s*Concrete\s*Blinding/i;
const SILO_CONCRETE_PATTERN = /^(\S+)\s+(\d+(?:\.\d+)?)\s*mm\s*thick/i;
const SILO_MESH_PATTERN = /2\s*x\s*layers\s+(\S+)\s+Mesh/i;
const SILO_OGCR_PATTERN = /OGCR\s+(\d+(?:\.\d+)?)\s*mm\s*thick/i;

// CARPARK row cp_5 ("Bindercourse, XXmm thick") — same 50/60/75/80/100mm
// option set as /groundworks/plot-drives' "xxmm DB Binder" dropdown. Matched
// by checking whether the row's own description includes each known mm
// value (its own text is short-form, not the DB Binder dropdown's full
// label), rather than a single regex.
const BINDERCOURSE_MM_VALUES = [50, 60, 75, 80, 100];

// Same full label texts as Compound.tsx's BINDERCOURSE_OPTIONS — if the
// sheet's cp_5 description already reads one of these verbatim (rather than
// the short "Bindercourse, XXmm thick" form), keep it exactly as-is.
const BINDERCOURSE_LABELS = [
  '50mm thick AC 20mm dense bin',
  '60mm thick AC 20mm dense bin',
  '75mm thick AC 20mm dense bin',
  '80mm thick AC 20mm dense bin',
  '100mm thick AC 20mm dense bin',
];

// SALES AREA rows sa_2/sa_3/sa_4 ("Base Course"/"Binder Course"/"Surface
// Course") — same option sets as /groundworks/plot-drives' "xxmm DB
// Base"/"xxmm DB Binder"/"xxmm DB Surface course" dropdowns. Matched by
// checking whether the row's own description includes each known label.
const SALES_BASE_COURSE_LABELS = ['100mm thick AC 32mm dense bin', '150mm thick AC 32mm dense bin'];
const SALES_BINDER_COURSE_LABELS = BINDERCOURSE_LABELS;
const SALES_SURFACE_COURSE_LABELS = [
  '20mm thick AC 6mm dense surf',
  '25mm thick AC 6mm dense surf',
  '25mm thick AC 10mm dense surf',
  '30mm thick AC 6mm dense surf',
  '30mm thick AC 10mm dense surf',
];

// SALES AREA row sa_6 ("Blockpaved footpaths") — same 60mm/80mm Concrete
// paviors option set as /groundworks/plot-drives' Block Paved surface dropdowns.
const SALES_BLOCK_PAVED_FOOTPATHS_LABELS = Object.values(EVALUATE_CODES)
  .filter((layer) => ['GWALPD002', 'GWALPD010', 'GWALPD006', 'GWALPD001', 'GWALPD009', 'GWALPD005'].includes(layer.code))
  .map((layer) => layer.label);

// SALES AREA row sa_8 ("Topsoiling, XXXmm thick, free issue site won") — mm
// value read straight off this row's own description.
const TOPSOILING_PATTERN = /^Topsoiling,?\s*(\d+(?:\.\d+)?)\s*mm\s*thick/i;

// TEMPORARY HAUL ROAD row thr_3 and WALKWAYS row wk_3 ("Supply and lay 6F2,
// XXXmm thick") — same idea as SUPPLY_LAY_6F2_PATTERN but with "and" instead
// of "&", matching these two sections' own wording.
const SUPPLY_AND_LAY_6F2_PATTERN = /Supply\s+and\s+lay\s+6F2,?\s*(\d+(?:\.\d+)?)\s*mm\s*thick/i;

// Static (non-configurable) row descriptions, keyed by id — the fallback
// match test below for every row id that has neither a *_PATTERN nor a
// label set of its own.
const COMPOUND_ROW_DESC: Record<string, string> = Object.fromEntries(
  COMPOUND_ROW_DEFS.filter((r) => r.rowType === 'data').map((r) => [r.id, r.description]),
);
const normalizeStatic = (s: string): string => s.trim().toLowerCase().replace(/\s+/g, ' ');

// Whether a sheet row's own description plausibly *is* the item identified by
// `rowId` — used to find which field a sheet row belongs to by what it says,
// not by its raw position in the sheet (a row the sheet leaves blank or omits
// would otherwise shift every later row in the section onto the wrong id).
// Configurable rows are matched via the same pattern/label set already used
// below to extract their config value, since their real description can vary
// (a different mm/grade/mesh/course choice) — a plain normalized-text
// equality check against the row's own static default would only match the
// unconfigured default. Every other row has no known variability, so its
// description is expected to appear in the sheet unchanged.
const descMatchesRow = (rowId: string, desc: string): boolean => {
  switch (rowId) {
    case 'cw_3': case 'cp_3': case 'ms_3':
      return SUPPLY_LAY_6F2_PATTERN.test(desc);
    case 'cw_4': case 'cp_4': case 'ms_4':
      return TYPE1_PATTERN.test(desc);
    case 'cw_11': case 'ms_5':
      return CONCRETE_MESH_PATTERN.test(desc);
    case 'sb_2': return SILO_SUB_BASE_PATTERN.test(desc);
    case 'sb_3': return SILO_BLINDING_PATTERN.test(desc);
    case 'sb_5': return SILO_CONCRETE_PATTERN.test(desc);
    case 'sb_6': return SILO_MESH_PATTERN.test(desc);
    case 'sb_7': return SILO_OGCR_PATTERN.test(desc);
    case 'sa_8': return TOPSOILING_PATTERN.test(desc);
    case 'thr_3': case 'wk_3':
      return SUPPLY_AND_LAY_6F2_PATTERN.test(desc);
    case 'cp_5':
      return BINDERCOURSE_LABELS.some((l) => desc.toLowerCase().includes(l.toLowerCase()))
        || BINDERCOURSE_MM_VALUES.some((v) => desc.includes(`${v}mm`));
    case 'sa_2':
      return SALES_BASE_COURSE_LABELS.some((l) => desc.toLowerCase().includes(l.toLowerCase()))
        || normalizeStatic(desc) === normalizeStatic(COMPOUND_ROW_DESC[rowId] ?? '');
    case 'sa_3':
      return SALES_BINDER_COURSE_LABELS.some((l) => desc.toLowerCase().includes(l.toLowerCase()))
        || normalizeStatic(desc) === normalizeStatic(COMPOUND_ROW_DESC[rowId] ?? '');
    case 'sa_4':
      return SALES_SURFACE_COURSE_LABELS.some((l) => desc.toLowerCase().includes(l.toLowerCase()))
        || normalizeStatic(desc) === normalizeStatic(COMPOUND_ROW_DESC[rowId] ?? '');
    case 'sa_6':
      return SALES_BLOCK_PAVED_FOOTPATHS_LABELS.some((l) => desc.toLowerCase().includes(l.toLowerCase()))
        || normalizeStatic(desc) === normalizeStatic(COMPOUND_ROW_DESC[rowId] ?? '');
    default:
      return normalizeStatic(desc) === normalizeStatic(COMPOUND_ROW_DESC[rowId] ?? '');
  }
};

const parseCompoundSheet = (rows: unknown[][]): { table: Record<string, FoulBuildUpRowData>; itemConfig: CompoundConfig } => {
  const result: Record<string, FoulBuildUpRowData> = {};
  const itemConfig: CompoundConfig = {};
  let currentIds: string[] = [];
  let pointer = 0;
  const triggered = new Set<string>();

  for (const row of rows) {
    const sectionKey = detectSectionTrigger(row, triggered);
    if (sectionKey) {
      triggered.add(sectionKey);
      currentIds = COMPOUND_SECTION_ROW_IDS[sectionKey] ?? [];
      pointer = 0;
      continue;
    }

    if (!currentIds.length) continue;

    // Require a description to confirm it's a data row
    const desc = String(row[DESC_COL] ?? '').trim();
    if (!desc) continue;

    // Find which item this sheet row actually describes — searching forward
    // from the last match rather than trusting position — so a row the
    // sheet leaves blank or omits entirely doesn't shift every later row in
    // the section (including the per-row config extraction keyed off rowId
    // below) onto the wrong field id.
    let matchIndex = -1;
    for (let i = pointer; i < currentIds.length; i++) {
      if (descMatchesRow(currentIds[i], desc)) {
        matchIndex = i;
        break;
      }
    }
    if (matchIndex === -1) continue; // no remaining item in this section describes this row

    const rowId = currentIds[matchIndex];
    pointer = matchIndex + 1;

    if (rowId === 'cw_3') {
      const match = desc.match(SUPPLY_LAY_6F2_PATTERN);
      if (match) itemConfig.compoundSupplyLay6F2Mm = parseFloat(match[1]);
    }
    if (rowId === 'cw_4') {
      const match = desc.match(TYPE1_PATTERN);
      if (match) itemConfig.compoundType1Mm = parseFloat(match[1]);
    }
    if (rowId === 'cp_3') {
      const match = desc.match(SUPPLY_LAY_6F2_PATTERN);
      if (match) itemConfig.carparkSupplyLay6F2Mm = parseFloat(match[1]);
    }
    if (rowId === 'cp_4') {
      const match = desc.match(TYPE1_PATTERN);
      if (match) itemConfig.carparkType1Mm = parseFloat(match[1]);
    }
    if (rowId === 'cp_5') {
      const fullLabel = BINDERCOURSE_LABELS.find((label) => desc.toLowerCase().includes(label.toLowerCase()));
      if (fullLabel) {
        itemConfig.carparkBindercourseLabel = fullLabel;
        const mm = BINDERCOURSE_MM_VALUES.find((v) => fullLabel.includes(`${v}mm`));
        if (mm !== undefined) itemConfig.carparkBindercourseMm = mm;
      } else {
        const mm = BINDERCOURSE_MM_VALUES.find((v) => desc.includes(`${v}mm`));
        if (mm !== undefined) itemConfig.carparkBindercourseMm = mm;
      }
    }
    if (rowId === 'sa_2') {
      const label = SALES_BASE_COURSE_LABELS.find((l) => desc.toLowerCase().includes(l.toLowerCase()));
      if (label) itemConfig.salesParkingBaseCourseLabel = label;
    }
    if (rowId === 'sa_3') {
      const label = SALES_BINDER_COURSE_LABELS.find((l) => desc.toLowerCase().includes(l.toLowerCase()));
      if (label) itemConfig.salesParkingBinderCourseLabel = label;
    }
    if (rowId === 'sa_4') {
      const label = SALES_SURFACE_COURSE_LABELS.find((l) => desc.toLowerCase().includes(l.toLowerCase()));
      if (label) itemConfig.salesParkingSurfaceCourseLabel = label;
    }
    if (rowId === 'sa_6') {
      const label = SALES_BLOCK_PAVED_FOOTPATHS_LABELS.find((l) => desc.toLowerCase().includes(l.toLowerCase()));
      if (label) itemConfig.salesParkingBlockPavedFootpathsLabel = label;
    }
    if (rowId === 'sa_8') {
      const match = desc.match(TOPSOILING_PATTERN);
      if (match) itemConfig.salesParkingTopsoilingMm = parseFloat(match[1]);
    }
    if (rowId === 'thr_3') {
      const match = desc.match(SUPPLY_AND_LAY_6F2_PATTERN);
      if (match) itemConfig.temporaryHaulRoadSupplyLay6F2Mm = parseFloat(match[1]);
    }
    if (rowId === 'wk_3') {
      const match = desc.match(SUPPLY_AND_LAY_6F2_PATTERN);
      if (match) itemConfig.walkwaysSupplyLay6F2Mm = parseFloat(match[1]);
    }
    if (rowId === 'ms_3') {
      const match = desc.match(SUPPLY_LAY_6F2_PATTERN);
      if (match) itemConfig.materialStoreSupplyLay6F2Mm = parseFloat(match[1]);
    }
    if (rowId === 'ms_4') {
      const match = desc.match(TYPE1_PATTERN);
      if (match) itemConfig.materialStoreType1Mm = parseFloat(match[1]);
    }
    if (rowId === 'ms_5') {
      const match = desc.match(CONCRETE_MESH_PATTERN);
      if (match) {
        itemConfig.materialStoreConcreteGrade = match[1];
        itemConfig.materialStoreConcreteMm = parseFloat(match[2]);
        itemConfig.materialStoreMeshType = match[3];
      }
    }
    if (rowId === 'cw_11') {
      const match = desc.match(CONCRETE_MESH_PATTERN);
      if (match) {
        itemConfig.compoundConcreteGrade = match[1];
        itemConfig.compoundConcreteMm = parseFloat(match[2]);
        itemConfig.compoundMeshType = match[3];
      }
    }
    if (rowId === 'sb_2') {
      const match = desc.match(SILO_SUB_BASE_PATTERN);
      if (match) itemConfig.siloSubBaseMm = parseFloat(match[1]);
    }
    if (rowId === 'sb_3') {
      const match = desc.match(SILO_BLINDING_PATTERN);
      if (match) itemConfig.siloBlindingMm = parseFloat(match[1]);
    }
    if (rowId === 'sb_5') {
      const match = desc.match(SILO_CONCRETE_PATTERN);
      if (match) {
        itemConfig.siloConcreteGrade = match[1];
        itemConfig.siloConcreteMm = parseFloat(match[2]);
      }
    }
    if (rowId === 'sb_6') {
      const match = desc.match(SILO_MESH_PATTERN);
      if (match) itemConfig.siloMeshType = match[1];
    }
    if (rowId === 'sb_7') {
      const match = desc.match(SILO_OGCR_PATTERN);
      if (match) itemConfig.siloOgcrMm = parseFloat(match[1]);
    }

    // The field slot is already consumed above regardless — this only
    // decides whether a quantity value gets stored for it.
    const rawQuant = row[QUANT_COL];
    if (rawQuant === null || rawQuant === undefined || rawQuant === '') continue;
    const qStr = String(rawQuant).trim();
    if (qStr.startsWith('#')) continue;
    const parsedQuant = parseFloat(qStr);
    if (isNaN(parsedQuant)) continue;
    // Round to the nearest whole number — imported quantities shouldn't carry
    // decimals through to the Compound page.
    const q = Math.round(parsedQuant);

    const rawEval = String(row[EVAL_COL] ?? '').trim();
    const evaluateCode = rawEval.startsWith('#') ? '' : rawEval;

    result[rowId] = { quant: q, evaluateCode };
  }

  return { table: result, itemConfig };
};

export const parseCompoundFromExcel = async (
  file: File,
): Promise<{ table: Record<string, FoulBuildUpRowData>; config: CompoundConfig }> => {
  const XLSX = await import('xlsx');
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array', raw: true });

  // Accept "Compound" (case-insensitive)
  const sheetName =
    workbook.SheetNames.find((n) => n.trim().toLowerCase() === 'compound') ??
    workbook.SheetNames.find((n) => /^compound$/i.test(n.trim()));

  if (!sheetName) {
    throw new Error(
      `Sheet "Compound" not found. Available sheets: ${workbook.SheetNames.join(', ')}`,
    );
  }

  const ws = workbook.Sheets[sheetName];
  const rows = XLSX.utils.sheet_to_json<unknown[]>(ws, {
    header: 1,
    raw: true,
    defval: '',
  }) as unknown[][];

  console.group('📋 Compound sheet import');
  console.log('Sheet name:', sheetName);
  console.log('Total rows:', rows.length);
  console.log('First 30 rows (raw):', rows.slice(0, 30));
  console.groupEnd();

  const { table, itemConfig } = parseCompoundSheet(rows);
  return { table, config: { ...extractCompoundSizes(rows), ...itemConfig } };
};
